import { supabase } from '../lib/supabase';
import { authenticatedFetch } from '../lib/api';
import type { Unit } from '../types';
import type { Json } from '../lib/database.types';

// NOTE: Direct Supabase queries (buildUnitQuery, getUnitsInBounds) were removed to enforce RBAC.
// All unit data MUST be fetched through the backend API (/api/v1/units) which applies
// proper authorization based on user_memberships. See api/routes/units.js for the authoritative
// data access layer with RBAC enforcement.

/**
 * Fetch all units with their latest status
 */
export async function getUnits(): Promise<Unit[]> {
  // Use backend API for all data (backend uses service role key to bypass RLS)
  // Demo mode detection is handled by hostname, not URL params
  const response = await authenticatedFetch('/api/v1/units');
  if (!response.ok) {
    throw new Error('Failed to fetch units from API');
  }
  const units = await response.json();
  return units; // Already in Unit format from backend transformation
}

/**
 * Fetch a single unit by unit_id
 */
export async function getUnitById(unitId: string): Promise<Unit | null> {
  // Use backend API for all data (backend uses service role key to bypass RLS)
  // Demo mode detection is handled by hostname, not URL params
  const response = await authenticatedFetch(`/api/v1/units/${unitId}`);
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error('Failed to fetch unit from API');
  }
  const unit = await response.json();
  return unit; // Already in Unit format from backend transformation
}

// NOTE: getUnitsInBounds was removed as it queried Supabase directly without RBAC.
// If viewport-based filtering is needed in the future, implement it as a backend
// API endpoint with proper RBAC (see api/routes/units.js for reference).

/**
 * Update HVAC settings for a unit
 * Updates the last_status JSON field with new settings
 */
export async function updateHvacSettings(
  unitId: string,
  settings: {
    targetTemp?: number;
    mode?: 'cool' | 'heat' | 'fan' | 'off';
  }
): Promise<void> {
  // Demo mode detection is handled by hostname, not URL params
  // Use Supabase for data updates
  // First, get the current device to preserve existing last_status data
  const { data: currentDevice, error: fetchError } = await supabase
    .from('hvac_devices')
    .select('last_status')
    .eq('unit_id', unitId)
    .single();

  if (fetchError) {
    if (fetchError.code === 'PGRST116') {
      console.error(`No HVAC device found for unit ${unitId}`);
      throw new Error(`No HVAC device found for unit ${unitId}`);
    }
    console.error('Error fetching HVAC device:', fetchError);
    throw new Error('Failed to fetch HVAC device');
  }

  // Merge new settings with existing last_status
  const currentStatus = (currentDevice?.last_status as Record<string, Json | undefined>) || {};
  const updatedStatus = {
    ...currentStatus,
    ...(settings.targetTemp !== undefined && { targetTemp: settings.targetTemp }),
    ...(settings.mode !== undefined && { mode: settings.mode }),
  };

  let errorMessage: string | undefined;

  try {
    const { data, error } = await supabase
      .from('hvac_devices')
      .update({
        last_status: updatedStatus,
      })
      .eq('unit_id', unitId)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        errorMessage = `No HVAC device found for unit ${unitId}`;
        console.error(errorMessage);
        throw new Error(errorMessage);
      }
      errorMessage = `Failed to update HVAC settings: ${error.message}`;
      console.error('Error updating HVAC:', error);
      throw new Error('Failed to update HVAC settings');
    }

    // Additional check: ensure data exists (defensive programming)
    if (!data) {
      errorMessage = `No HVAC device found for unit ${unitId}`;
      console.error(errorMessage);
      throw new Error(errorMessage);
    }
  } catch (err) {
    // Extract error message if not already set
    if (!errorMessage) {
      errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    }
    // Log the failed control command
    await logControlCommand(unitId, 'hvac', 'update', settings, false, errorMessage);
    // Re-throw to maintain existing error handling behavior
    throw err;
  }

  // Log the successful control command
  await logControlCommand(unitId, 'hvac', 'update', settings, true);
}

/**
 * Log a control command for audit trail
 */
async function logControlCommand(
  unitId: string,
  component: string,
  action: string,
  payload: Json,
  success: boolean,
  errorMessage?: string
): Promise<void> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) {
    console.error('Error fetching user for audit log:', authError);
    // Continue without user_id if auth fails
  }

  // Only log if we have a user_id (required field)
  if (!user?.id) {
    console.warn('Cannot log control command: no authenticated user');
    return;
  }

  const { error: logError } = await supabase.from('control_command_logs').insert({
    unit_id: unitId,
    user_id: user.id,
    component,
    action,
    request_payload: payload,
    status: success ? 'success' : 'error',
    response_payload: errorMessage ? { error: errorMessage } : null,
  });

  if (logError) {
    console.error('Error logging control command:', logError);
    // Don't throw - logging failures shouldn't break the main operation
  }
}

/**
 * Default values for missing system data
 * These are used when data is not available from the database or last_status JSON
 */
const DEFAULTS = {
  STARLINK: {
    signalStrength: 0, // dBm - 0 indicates no signal/no data
    downlink: 0, // Mbps
    uplink: 0, // Mbps
    latency: 0, // ms
  },
  SOLAR: {
    loadOutput: 0, // kW - consumption/load output
    gridStatus: 'disconnected' as const, // 'connected' | 'disconnected'
  },
  CHARGER: {
    voltage: 0, // V
    current: 0, // A
  },
  HVAC: {
    fanSpeed: 'med' as const, // 'low' | 'med' | 'high'
    temperature: 72, // °F
    targetTemp: 72, // °F
  },
} as const;

/**
 * Extract numeric value from JSON object with fallback
 * Always returns a number (uses defaultValue if not found)
 */
function extractNumber(json: unknown, key: string, defaultValue: number): number {
  if (!json || typeof json !== 'object') return defaultValue;
  const value = (json as Record<string, unknown>)[key];
  if (value === null || value === undefined) return defaultValue;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
}

/**
 * Extract optional numeric value from JSON object
 * Returns undefined if not found, otherwise returns the number
 */
function extractOptionalNumber(json: unknown, key: string): number | undefined {
  if (!json || typeof json !== 'object') return undefined;
  const value = (json as Record<string, unknown>)[key];
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

/**
 * Extract string value from JSON object with fallback
 */
function extractString(json: unknown, key: string, defaultValue: string): string {
  if (!json || typeof json !== 'object') return defaultValue;
  const value = (json as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : defaultValue;
}

/**
 * Extract Solar metrics from last_status JSON
 * Expected fields: loadOutput (kW), gridStatus ('connected' | 'disconnected')
 */
function extractSolarMetrics(lastStatus: unknown): {
  loadOutput: number;
  gridStatus: 'connected' | 'disconnected';
} {
  if (!lastStatus) {
    return { ...DEFAULTS.SOLAR };
  }

  const gridStatus = extractString(lastStatus, 'gridStatus', DEFAULTS.SOLAR.gridStatus);
  const validGridStatus = gridStatus === 'connected' ? 'connected' : 'disconnected';

  return {
    loadOutput: extractNumber(lastStatus, 'loadOutput', DEFAULTS.SOLAR.loadOutput),
    gridStatus: validGridStatus,
  };
}

/**
 * Extract HVAC metrics from last_status JSON
 * Expected fields: fanSpeed ('low' | 'med' | 'high'), temperature (°F), targetTemp (°F)
 */
function extractHVACMetrics(lastStatus: unknown): {
  fanSpeed: 'low' | 'med' | 'high';
  temperature: number;
  targetTemp: number;
} {
  if (!lastStatus) {
    return { ...DEFAULTS.HVAC };
  }

  const fanSpeed = extractString(lastStatus, 'fanSpeed', DEFAULTS.HVAC.fanSpeed);
  const validFanSpeed = ['low', 'med', 'high'].includes(fanSpeed)
    ? (fanSpeed as 'low' | 'med' | 'high')
    : DEFAULTS.HVAC.fanSpeed;

  return {
    fanSpeed: validFanSpeed,
    temperature: extractNumber(lastStatus, 'temperature', DEFAULTS.HVAC.temperature),
    targetTemp: extractNumber(lastStatus, 'targetTemp', DEFAULTS.HVAC.targetTemp),
  };
}

const STALE_THRESHOLD_MS = 15 * 60_000;

function isFresh(isoTimestamp: string | null | undefined): boolean {
  if (!isoTimestamp) return false;
  return Date.now() - Date.parse(isoTimestamp) < STALE_THRESHOLD_MS;
}

/**
 * Determine unit status across all live sources.
 *
 * A unit is "online" if ANY source reported recently — a dead Starlink dish
 * on a trailer whose Sol-Ark is reporting is a connectivity fault, not an
 * offline trailer. Matches the backend's deriveUnitStatus() logic.
 */
export function determineStatus(
  latestStatus: { starlink_status?: string | null } | null | undefined,
  unitStatus: string | null | undefined,
  solarkLastSeen?: string | null
): 'online' | 'offline' | 'idle' | 'unknown' {
  const starlinkStatus = latestStatus?.starlink_status;

  if (starlinkStatus === 'online') return 'online';
  if (starlinkStatus === 'idle' || unitStatus === 'idle') return 'idle';

  if (isFresh(solarkLastSeen)) return 'online';

  if (starlinkStatus === 'offline') return 'offline';
  if (starlinkStatus === 'suspended') return 'offline';
  if (starlinkStatus === 'unknown') return 'unknown';

  if (unitStatus === 'online') return 'online';
  if (unitStatus === 'offline') return 'offline';

  return 'unknown';
}

/**
 * Get last update timestamp with safe fallbacks
 * Priority: snapshot updated_at > snapshot_at > unit updated_at > current time
 */
function getLastUpdate(
  latestStatus: { updated_at?: string | null; snapshot_at?: string | null } | null | undefined,
  unitUpdatedAt: string | null | undefined
): string {
  if (latestStatus?.updated_at) return latestStatus.updated_at;
  if (latestStatus?.snapshot_at) return latestStatus.snapshot_at;
  if (unitUpdatedAt) return unitUpdatedAt;
  
  // Last resort: current time (should rarely happen)
  const now = new Date().toISOString();
  console.warn('No timestamp available for unit, using current time as fallback');
  return now;
}

/** Raw device sub-row (starlink/solar/charger/hvac) as returned by the backend. */
interface RawDeviceRow {
  id?: string | null;
  external_terminal_id?: string | null;
  last_status?: unknown;
  last_seen_at?: string | null;
}

/** Raw status snapshot sub-row as returned by the backend. */
interface RawStatusSnapshot {
  starlink_status?: string | null;
  updated_at?: string | null;
  snapshot_at?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

/** Raw unit row as returned by the backend `/api/v1/units` transformation. */
interface RawUnitRow {
  id: string;
  unit_code: string;
  name?: string | null;
  status?: string | null;
  updated_at?: string | null;
  city?: string | null;
  state_province?: string | null;
  location_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  location?: { lat?: number | null; lng?: number | null } | null;
  primary_image_url?: string | null;
  company_id: string;
  primary_group_id: string | null;
  unit_status_snapshots?: RawStatusSnapshot[];
  starlink_terminals?: RawDeviceRow[];
  tesla_chargers?: RawDeviceRow[];
  solark_inverters?: RawDeviceRow[];
  hvac_devices?: RawDeviceRow[];
}

/**
 * Transform database Unit to frontend Unit type
 * Extracts data from last_status JSON fields where available, uses documented defaults otherwise
 *
 * @internal Exported for testing purposes
 */
export function transformUnit(unit: RawUnitRow): Unit {
  const latestStatus = unit.unit_status_snapshots?.[0];
  const starlink = unit.starlink_terminals?.[0];
  const charger = unit.tesla_chargers?.[0];
  const solar = unit.solark_inverters?.[0];
  const hvac = unit.hvac_devices?.[0];

  // Extract location with safe fallback
  // Prioritize geocoded coordinates from status snapshot (updated from Starlink)
  // over stale unit table coordinates
  // Format address from geocoded data: "City, State" or "City" or "Location unknown"
  let formattedAddress = 'Location unknown';
  if (unit.city && unit.state_province) {
    formattedAddress = `${unit.city}, ${unit.state_province}`;
  } else if (unit.city) {
    formattedAddress = unit.city;
  } else if (unit.location_code) {
    // Fallback to location code if geocoding hasn't run yet
    formattedAddress = unit.location_code;
  }

  const location = {
    lat: latestStatus?.latitude ?? unit.latitude ?? unit.location?.lat ?? 0,
    lng: latestStatus?.longitude ?? unit.longitude ?? unit.location?.lng ?? 0,
    address: formattedAddress,
  };

  // Log missing critical data for observability
  if (!latestStatus) {
    console.warn(`[transformUnit] No status snapshot found for unit ${unit.unit_code} (${unit.id})`);
  }
  if (location.lat === 0 && location.lng === 0) {
    console.warn(`[transformUnit] No location data for unit ${unit.unit_code} (${unit.id})`);
  }

  // Extract Solar metrics
  const solarMetrics = extractSolarMetrics(solar?.last_status);
  if (!solar?.last_status && solar) {
    console.debug(`[transformUnit] No last_status JSON for Solar inverter on unit ${unit.unit_code}, using defaults`);
  }

  // Extract HVAC metrics
  const hvacMetrics = extractHVACMetrics(hvac?.last_status);
  if (!hvac?.last_status && hvac) {
    console.debug(`[transformUnit] No last_status JSON for HVAC device on unit ${unit.unit_code}, using defaults`);
  }

  // Determine status across all live sources
  const solarkLastSeen = solar?.last_seen_at ?? null;
  const status = determineStatus(latestStatus, unit.status, solarkLastSeen);
  const lastUpdate = getLastUpdate(latestStatus, unit.updated_at);

  // Build unit image URL
  // Use denormalized primary_image_url from units table if available
  // Otherwise fall back to static asset based on unit_code (not UUID id)
  const image = unit.primary_image_url || `/assets/units/${unit.unit_code || 'placeholder'}.jpg`;

  return {
    id: unit.id,  // units.id (UUID) - always use this as primary key
    unit_code: unit.unit_code,  // User-facing ID like "1RE001TCA"
    name: unit.name || unit.unit_code || 'Unknown',
    starlinkId: starlink?.external_terminal_id || starlink?.id || '',
    status,
    location,
    image,
    lastUpdate,
    company_id: unit.company_id,
    primary_group_id: unit.primary_group_id,
    systems: {
      solar: {
        enabled: solar !== null && solar !== undefined,
        battery: extractNumber(solar?.last_status, 'batteryLevel', 0),
        generation: extractNumber(solar?.last_status, 'generation', 0),
        consumption: solarMetrics.loadOutput,
        status: extractString(solar?.last_status, 'status', 'idle') as 'charging' | 'discharging' | 'idle',
      },
      charger: {
        enabled: charger !== null && charger !== undefined,
        status: extractString(charger?.last_status, 'status', 'idle') as 'charging' | 'idle' | 'disconnected',
        powerIn: extractNumber(charger?.last_status, 'powerIn', 0),
        powerOut: extractNumber(charger?.last_status, 'powerOut', 0),
        batteryLevel: extractOptionalNumber(charger?.last_status, 'batteryLevel'),
        timeRemaining: extractOptionalNumber(charger?.last_status, 'timeRemaining'),
      },
      hvac: {
        enabled: hvac !== null && hvac !== undefined,
        temperature: hvacMetrics.temperature,
        targetTemp: hvacMetrics.targetTemp,
        mode: extractString(hvac?.last_status, 'mode', 'off') as 'cool' | 'heat' | 'fan' | 'off',
        fanSpeed: hvacMetrics.fanSpeed,
        humidity: extractOptionalNumber(hvac?.last_status, 'humidity'),
      },
    },
    // Note: alerts and alertType are not yet available from the database schema
    // They will be populated when alert system is implemented
  };
}